<template>
  <div>
    <v-card height="90vh"
    :style="cardDynamicStyle"
    >
      <v-card-title ref="cardTitle" :key="updateReportsList">
        Reports
        <v-spacer></v-spacer>
        <v-text-field
          dense
          hide-details
          v-model="search"
          :disabled="!reports.length"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          @input="onChangeSearchVal"
        ></v-text-field>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-subtitle ref="cardSubtitle">
        <v-row>
          <v-col cols="12" md="6" lg="3">
            <RangeDatePickerMenu
              v-model="dateRange"
              showButtons
            />
          </v-col>
          <v-col cols="12" md="6" lg="3">
            <v-select
              dense
              hide-details
              label="Group By"
              multiple
              v-model="groupBy"
              :disabled="!groupByItems.length"
              :items="groupByItems"
              item-text="title"
              item-value="key"
              @change="groupFieldChanged"
            ></v-select>
          </v-col>
          <v-col cols="12" lg="6">
            <div class="d-flex align-center flex-wrap">
              <v-btn
                class="ma-1"
                :disabled="!canShowReports || loading"
                :loading="loading"
                small
                color="primary"
                @click="getReports(false)"
              >
                Show Reports
              </v-btn>
              <v-btn
                v-if="selectedReports.length"
                small
                color="primary"
                class="ma-1"
                :disabled="assignedTo === -1"
                @click="moveSelectedReports(selectedReports)"
              >
                Move Selected to
              </v-btn>
              <v-btn
                v-if="selectedReports.length"
                small
                color="orange darken-2 white--text"
                class="ma-1"
                @click="handleBatchUpdateForSelected"
              >
                Set Invoice Dur to 0
              </v-btn>
              <v-btn
                v-if="selectedReports.length"
                small
                color="red darken-2 white--text"
                class="ma-1"
                @click="deleteReport(selectedReports)"
              >
                Delete Selected
              </v-btn>
              <v-spacer></v-spacer>
              <TableColumnConfigMenu
                class="ma-1"
                icon-only
                :default-headers="defaultHeaders"
                :column-config="columnConfig"
                @save="onColumnConfigSave"
              />
            </div>
          </v-col>
        </v-row>
      </v-card-subtitle>
      <v-divider></v-divider>
      <v-card-text>
        <div class="d-flex align-center grand-total" v-if="reports.length" >
          <span>
            Reports: {{ reports.length }}{{ filteredReports.length === reports.length ? '' : `/ Filtered: ${filteredReports.length}` }}
          </span>
          <v-spacer></v-spacer>
          <div class="total-flex-container">
            <div class="total-flex-col col-reported">
              <div class="total-flex-header">Reported</div>
              <div class="total-flex-value">{{ totalsForGroups.totalReported.toFixed(2) }}</div>
            </div>
          <div class="total-flex-col col-paid">
              <div class="total-flex-header">Paid</div>
               <div class="total-flex-value">{{ totalsForGroups.totalPaid.toFixed(2) }}</div>
              </div>
            <div class="total-flex-col col-invoice">
              <div class="total-flex-header">Invoice</div>
              <div class="total-flex-value">{{ totalsForGroups.totalInvoice.toFixed(2) }}</div>
            </div>
            <div v-if="isGroupedByEmployee" class="total-flex-col col-vacation">
              <div class="total-flex-header">Vacations Left</div>
              <div class="total-flex-value">{{ totalsForGroups.vacationLeft.toFixed(2) }}</div>
            </div>
            <div v-if="isGroupedByEmployee" class="total-flex-col col-sick">
              <div class="total-flex-header">Sick Leaves Left</div>
              <div class="total-flex-value">{{ totalsForGroups.sickLeaveLeft.toFixed(2) }}</div>
            </div>
          </div>
          <v-btn
            x-small
            color="primary"
            class="ml-3 export-to-xlsx-btn"
            @click.prevent="exportToXLSX(groups)">
            Export All To XLSX
          </v-btn>
        </div>
        <v-divider v-if="reports.length"></v-divider>
        <div
          class="overflow-y-auto"
          :style="{
            maxHeight: selectedReports.length ? `calc(86vh - ${reportsContainerMaxHeight}px)` : `calc(90vh - ${reportsContainerMaxHeight}px)`,
            height: '100vh'
          }"
          ref="scrollAreaRef"
        >
          <div
            v-for="group in paginatedGroups"
            :key="group.key"
            class="group-container"
            :class="{ 'group-container--auto': paginatedGroups.length > 1 }"
          >
            <div
              class="group-title primary white--text"
              v-if="groupBy.length"
              :ref="`group_${group.key}`"
              @click="toggleGroup(group)"
            >
              <v-icon color="white">
                {{ group.open ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
                {{ group.title | groupTitleFilter }}
              <v-spacer></v-spacer>
              <div class="total-flex-container">
                <div class="total-flex-col total-flex-col--group col-reported">
                  <div class="total-flex-value">{{ group.total.totalReported.toFixed(2) }}</div>
                </div>
              <div class="total-flex-col total-flex-col--group col-paid">
                  <div class="total-flex-value">{{ group.total.totalPaid.toFixed(2) }}</div>
                </div>
                <div class="total-flex-col total-flex-col--group col-invoice">
                  <div class="total-flex-value">{{ group.total.totalInvoice.toFixed(2) }}</div>
                </div>
                <div v-if="isGroupedByEmployee" class="total-flex-col total-flex-col--group col-vacation">
                  <div class="total-flex-value">{{ group.total.vacationLeft.toFixed(2) }}</div>
                </div>
                <div v-if="isGroupedByEmployee" class="total-flex-col total-flex-col--group col-sick">
                  <div class="total-flex-value">{{ group.total.sickLeaveLeft.toFixed(2) }}</div>
                </div>
              </div>
              <v-btn
                x-small
                color="white"
                class="ml-3 export-to-xlsx-btn"
                outlined
                @click.prevent="exportToXLSX(group)"
              >
                Export To XLSX
              </v-btn>
            </div>
            <ReportItems
              v-show="group.open"
              :pagination="groupPagination(group.key)"
              v-model="group.reports"
              :key="group.reports.length + '_' + reportsItemKey"
              :loading="loading"
              @rowClick="onRowClick"
              @onContextMenu="onContextMenu"
              :columns="resolvedColumns"
              :groupRef="groupBy.length ? $refs[`group_${group.key}`] : null"
              @deleteReport="deleteReport"
              @addSelectedReport="addSelectedReport"
              @removeSelectedReport="removeSelectedReport"
              :group="group"
              :is-grouped-by-employee="isGroupedByEmployee"
              @batchUpdateInvoiceDur="handleBatchUpdateInvoiceDur"
            />
            <div
              class="pagination-container d-flex py-2"
              v-if="group.reports.length && group.open && !loading"
            >
              <v-spacer></v-spacer>
              <Pagination
                :paginationInf="groupPagination(group.key)"
                @pageChange="changeCurrentPageForGroup(group.key, $event); scrollTop()"
              />
            </div>
          </div>
          <div
            class="pagination-container d-flex py-2"
            v-if="reports.length && groups.length > 100 && !loading"
          >
            <v-spacer></v-spacer>
            <Pagination
              :paginationInf="paginationForGroup"
              @pageChange="paginationForGroup.currentPage = $event; scrollTop()"
            />
          </div>
        </div>
      </v-card-text>
      <v-menu
        v-model="contextMenu.show"
        :position-x="contextMenu.x"
        :position-y="contextMenu.y"
        :close-on-content-click="true"
        absolute
        offset-y
      >
        <v-list>
          <v-list-item @click="onRowClick({report: contextMenu.report})">
            <v-list-item-title>{{ contextMenu.report && contextMenu.report.Status === 'C' ? 'View' : 'Edit' }}</v-list-item-title>
          </v-list-item>
          <v-list-item @click="deleteReport(contextMenu.report)" v-if="contextMenu.report && !contextMenu.report.Locked">
            <v-list-item-title>Delete</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <EditReportDialog
        :key="reportingFor ? reportingFor.Task_ID : 'default'"
         v-model="editReport"
        @updateReport="onUpdateReport"
        @createReport="onCreateReport"
      />
      <DeleteReportDialog
        v-model="deleteReportData"
        @reportDeleted="deleteReportsFromState"
      />
      <v-dialog
        v-model="alertDialog"
        max-width="400px"
      >
        <v-card>
          <v-card-text class="py-1">
            {{ alertDialogMsg }}
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn small color="primary" @click="alertDialog = !alertDialog">ok</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
    <MoveReportingToDialog
      v-if="showMoveToReportsDialog"
      v-model="showMoveToReportsDialog"
      :reportingsToMove="selectedReports"
      :assignedTo="assignedTo"
      @refreshResults="refreshResults($event)"
    />
    <ConfirmOrDeniedDialog
      v-if="confirmDialog.show"
      v-model="confirmDialog.show"
      mode="confirmation"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      confirmBtnName="Update"
      @onUpdateClick="executeBatchUpdate"
      @onCancelClick="cancelBatchUpdate"
      @onCloseClick="cancelBatchUpdate"
    />
  </div>
</template>
<script>
import moment from 'moment'
import { tableColumnConfigMixin } from '@/mixins/tableColumnConfigMixin'
const xlsx = require('json-as-xlsx')

export default {
  name: 'ReportsList',
  mixins: [tableColumnConfigMixin],
  components: {
    ReportItems: () => import('@/components/manager_reports/ReportItems.vue'),
    RangeDatePickerMenu: () => import('@/components/global/RangeDatePickerMenu.vue'),
    TableColumnConfigMenu: () => import('@/components/global/TableColumnConfigMenu.vue'),
    EditReportDialog: () => import('@/components/manager_reports/EditReportDialog.vue'),
    DeleteReportDialog: () => import('@/components/manager_reports/DeleteReportDialog.vue'),
    Pagination: () => import('@/components/manager_reports/Pagination.vue'),
    MoveReportingToDialog: () => import('@/components/manager_reports/MoveReportingToDialog.vue'),
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    value: {
      required: true
    },
    addReportingFor: {
      required: false
    },
    refreshResultsKey: {
      required: false
    },
    filterClosedFoldersTasks: {
      required: false
    },
    isRedirected: {
      required: false,
      default: false
    }
  },
  data () {
    return {
      loading: false,
      reportsItemKey: Date.now(),
      reports: [],
      assignedTo: -1,
      alertDialog: false,
      alertDialogMsg: '',
      selectedReports: [],
      showMoveToReportsDialog: false,
      search: '',
      groupBy: [],
      contextMenu: {
        show: false,
        x: 0,
        y: 0,
        report: null
      },
      reportsPerPage: 100,
      deleteReportData: {
        show: false,
        reportID: null
      },
      confirmDialog: {
        show: false,
        title: '',
        message: '',
        reportIds: []
      },
      columns: [
        {
          title: '',
          key: 'actions',
          colWidth: '35px',
          groupable: false,
          exportable: false,
          filterable: false
        },
        {
          title: 'Locked',
          key: 'Locked',
          colWidth: '50px',
          groupable: false,
          exportable: false,
          filterable: false
        },
        {
          title: 'Employee',
          key: 'Employee_FullName',
          colWidth: '70px',
          groupable: true,
          exportable: true,
          filterable: true
        },
        {
          title: 'Project',
          key: 'Project_Name',
          colWidth: '70px',
          groupable: true,
          exportable: true,
          filterable: true
        },
        {
          title: 'Department',
          key: 'Dep_Name',
          colWidth: '80px',
          groupable: true,
          exportable: true,
          filterable: true
        },
        {
          title: 'Task',
          key: 'Task_Name',
          colWidth: '90px',
          groupable: true,
          exportable: true,
          filterable: true
        },
        {
          title: 'Start Date',
          key: 'Start_Date',
          colWidth: '80px',
          groupable: true,
          exportable: true,
          filterable: true
        },
        {
          title: 'Rept. Dur.',
          key: 'Real_Dur',
          colWidth: '70px',
          groupable: false,
          exportable: true,
          filterable: true
        },
        {
          title: 'Paid Dur.',
          key: 'Report_Dur',
          colWidth: '70px',
          groupable: false,
          exportable: true,
          filterable: true
        },
        {
          title: 'Inv. Dur.',
          key: 'Invoice_Dur',
          colWidth: '80px',
          groupable: false,
          exportable: true,
          filterable: true
        },
        {
          title: 'Activity Type',
          key: 'Activity_Type_Description',
          colWidth: '80px',
          groupable: true,
          exportable: true,
          filterable: true
        },
        {
          title: 'Vacations Left',
          key: 'Vacation_Left',
          colWidth: '110px',
          groupable: false,
          exportable: true,
          hide: true,
          filterable: false
        },
        {
          title: 'Sick Leaves Left',
          key: 'Sick_Leave_Left',
          colWidth: '110px',
          groupable: false,
          exportable: true,
          hide: true,
          filterable: false
        },
        {
          title: 'Story Points',
          key: 'Story_Points',
          colWidth: '80px',
          groupable: false,
          exportable: true,
          filterable: true
        },
        {
          title: 'Time Spent',
          key: 'Timespent',
          colWidth: '80px',
          groupable: false,
          exportable: true,
          filterable: true
        },
        {
          title: 'Status Name',
          key: 'Status_Name',
          colWidth: '80px',
          groupable: false,
          exportable: true,
          filterable: true
        },
        {
          title: 'Locked',
          key: 'Locked_Descr',
          groupable: false,
          exportable: false,
          hide: true,
          filterable: false
        },
        {
          title: 'Description',
          key: 'Description',
          colWidth: '25%',
          groupable: false,
          exportable: true,
          filterable: true
        },
        {
          title: 'Status',
          key: 'Status',
          groupable: true,
          hide: true,
          exportable: false,
          filterable: false
        }
      ],
      dateRange: [],
      editReport: {
        report: {},
        show: false
      },
      pagination: [],
      groups: [],
      paginationForGroup: {
        currentPage: 1,
        totalPages: null
      },
      currentPage: 1,
      totalsForGroups: {
        totalReported: 0,
        totalPaid: 0,
        totalInvoice: 0,
        vacationLeft: 0,
        sickLeaveLeft: 0
      },
      tableColumnConfigKey: 'manager_reports',
      columnConfig: null
    }
  },
  computed: {
    reportToUpdate: {
      get () {
        return this.$store.getters.getReportToUpdate
      },
      set (val) {
        this.$store.dispatch('updateManagerReportState', { key: 'updateReport', value: val })
      }
    },
    settings () {
      return this.$store.getters.getSettings
    },
    reportingFor: {
      get () {
        if (this.addReportingFor) {
          this.createReport(this.addReportingFor)
        }
        return this.addReportingFor
      },
      set (val) {
        if (val === null) {
          this.$emit('reportingWasAdded')
        }
      }
    },
    updateReportsList: {
      get () {
        const update = this.$store.getters.updateReportsList
        if (update && this.canShowReports) {
          this.getReports(true)
        }
        return update
      },
      set (val) {
        this.$store.dispatch('updateReportsList', { update: val })
      }
    },
    defaultHeaders () {
      return this.columns
        .filter(c => c.key !== 'actions')
        .map(c => ({ ...c, value: c.key, text: c.title }))
    },
    resolvedColumns () {
      const actionsColumn = this.columns.find(c => c.key === 'actions')
      const resolved = this.applyTableColumnConfig(this.defaultHeaders)
      const rest = resolved.map(h => ({ ...h, hide: false }))
      return actionsColumn ? [actionsColumn, ...rest] : rest
    },
    groupByItems () {
      return this.resolvedColumns.filter(item => item.groupable).sort((a, b) => (a.title.toString()).localeCompare(b.title.toString()))
    },
    paginatedGroups () {
      const groups = [...this.groups]
      return groups.slice((this.paginationForGroup.currentPage - 1) * this.reportsPerPage, this.paginationForGroup.currentPage * this.reportsPerPage)
    },
    reportsContainerMaxHeight () {
      const cardTitle = this.$refs.cardTitle ? this.$refs.cardTitle.offsetHeight + 28 : 92
      const cardSubtitle = this.$refs.cardSubtitle ? this.$refs.cardSubtitle.offsetHeight + 28 : 92
      return cardTitle + cardSubtitle
    },
    canShowReports () {
      if (this.value.selectedProjects && this.value.selectedProjects.length) {
        return true
      }
      if (this.value.selectedTasks && this.value.selectedTasks.length) {
        return true
      }
      if (this.value.selectedFolders && this.value.selectedFolders.length) {
        return true
      }
      if (this.value.selectedEmployee && this.value.selectedEmployee.length) {
        return true
      }
      return false
    },
    groupPagination: {
      get () {
        return key => {
          return this.pagination.filter(p => p.key === key)[0]
        }
      },
      set (val) {
        const paginationIndex = this.pagination.findIndex(p => p.key === val.key)
        if (paginationIndex > -1) {
          this.$set(this.pagination, paginationIndex, val)
        }
      }
    },
    isGroupedByEmployee () {
      return this.groupBy.length && this.groupBy.length === 1 && this.groupBy[0] === 'Employee_FullName'
    },
    filteredReports () {
      let res = this.reports
      if (this.search && res.length) {
        const escapeRegex = this.search.replace(/[|\\{}()[\]^$+*?.]/g, '\\$&')
        const searchRegex = new RegExp(escapeRegex, 'gi')
        res = this.reports.filter(r => {
          searchRegex.lastIndex = 0
          let searchStr = ''
          for (let i = 0; i < this.filterByItems.length; i++) {
            searchStr += r[this.filterByItems[i]]
          }
          return searchRegex.test(searchStr)
        })
      }
      if (this.groupBy && this.groupBy.length) {
        this.groupBy.forEach(key => {
          res = res.sort((a, b) => a[key].localeCompare(b[key]))
        })
      }
      return res
    },
    user () {
      return this.$store.getters.getUser
    },
    activityTypes () {
      return this.$store.getters.getActivityTypes
    },
    filterByItems () {
      return this.resolvedColumns.filter(el => el.filterable).map(el => el.key)
    },
    employeeRoles () {
      return this.$store.getters.getEmployeeRoles
    },
    cardDynamicStyle () {
      if (this.groupBy.length === 1 && this.groupBy[0] === 'Employee_FullName') {
        return {
          width: '100%',
          minWidth: '800px',
          transition: 'width 0.3s ease'
        }
      }
      return {
        width: '100%',
        transition: 'width 0.3s ease'
      }
    }
  },
  filters: {
    groupTitleFilter (val) {
      return val.split(',').map(t => {
        switch (t.trim()) {
          case 'C': return ' Closed'
          case 'U': return ' Unclosed'
          default: return t
        }
      }).join(',')
    },
    groupTotal (val) {
      return `<table>
        <tr>
          <td> Reported: ${val.totalReported.toFixed(2)} </td>
          <td> Paid: ${val.totalPaid.toFixed(2)} </td>
          <td> Invoice: ${val.totalInvoice.toFixed(2)} </td>
        </tr>
      </table>`
    }
  },
  mounted () {
    this.loadTableColumnConfig()
  },
  methods: {
    canReport (projectName) {
      const excludedProjects = ['DC Vacations', 'DC Sick-Leave']
      const isFinancier = this.employeeRoles.financier.val
      if (!isFinancier && excludedProjects.includes(projectName)) {
        this.$store.dispatch('setError', { errorMsg: 'Only Financier role can Save/Update this project report' })
        return false
      }
      return true
    },
    async refreshResults (e) {
      this.selectedReports = []
      await this.getReports(false)
      this.$emit('reportsUpdated', e)
    },
    moveSelectedReports (selectedReports) {
      this.showMoveToReportsDialog = true
    },
    totalsForAllGroups () {
      if (this.groups.length > 1) {
        this.totalsForGroups.totalReported = 0
        this.totalsForGroups.totalPaid = 0
        this.totalsForGroups.totalInvoice = 0
        this.totalsForGroups.vacationLeft = 0
        this.totalsForGroups.sickLeaveLeft = 0
        for (let i = 0; i < this.groups.length; i++) {
          this.totalsForGroups.totalReported += this.groups[i].total.totalReported
          this.totalsForGroups.vacationLeft += this.groups[i].total.vacationLeft
          this.totalsForGroups.sickLeaveLeft += this.groups[i].total.sickLeaveLeft
          this.totalsForGroups.totalPaid += this.groups[i].total.totalPaid
          this.totalsForGroups.totalInvoice += this.groups[i].total.totalInvoice
        }
      } else {
        if (this.groups.length === 1) {
          this.totalsForGroups.totalReported = this.groups[0].total.totalReported
          this.totalsForGroups.vacationLeft = this.groups[0].total.vacationLeft
          this.totalsForGroups.sickLeaveLeft = this.groups[0].total.sickLeaveLeft
          this.totalsForGroups.totalPaid = this.groups[0].total.totalPaid
          this.totalsForGroups.totalInvoice = this.groups[0].total.totalInvoice
        }
      }
    },
    createReport (task) {
      this.onRowClick({
        report: {
          Activity_Type: this.settings ? this.settings.defaultActivityType : this.activityTypes[0].Activity_Type,
          Description: null,
          Employee_ID: task.Assigned_To,
          Invoice_Dur: null,
          Project_ID: task.Project_ID,
          Project_Name: task.Project_Name,
          Real_Dur: '00:00',
          Report_Dur: null,
          Start_Time: '00:00',
          Reporting_ID: null,
          Start_Date: new Date().toISOString().substring(0, 10),
          Story_Points: null,
          Timespent: null,
          Status_Name: null,
          Task_ID: task.Task_ID,
          Name: task.Name,
          Full_Name: task.Full_Name
        }
      })
    },
    handleBatchUpdateInvoiceDur (reportIds) {
      if (!reportIds.length) return

      this.confirmDialog = {
        show: true,
        title: 'Confirm Update',
        message: `Are you sure you want to set Invoice Duration to 00:00 for ${reportIds.length} reports?`,
        reportIds: reportIds
      }
    },
    async executeBatchUpdate () {
      const reportIds = this.confirmDialog.reportIds
      if (!reportIds.length) return

      this.loading = true
      try {
        await this.axios.post('/api/batch-update-invoice-duration', { reportIds }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        })

        this.reports.forEach(report => {
          if (reportIds.includes(report.Reporting_ID)) {
            report.Invoice_Dur = '00:00'
          }
        })

        this.reportsItemKey = Date.now()
        const openGroups = this.groups.filter(gr => gr.open).map(gr => gr.key)
        this.setGroup(openGroups)
        this.setPagination()
        this.totalsForAllGroups()

        this.$store.dispatch('setSuccess', { successMsg: `${reportIds.length} reports successfully updated.` })
      } catch (error) {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + (error.response ? error.response.data : '') })
      } finally {
        this.loading = false
        this.cancelBatchUpdate()
      }
    },
    handleBatchUpdateForSelected () {
      if (!this.selectedReports.length) return

      this.confirmDialog = {
        show: true,
        title: 'Confirm Update',
        message: `Are you sure you want to set Invoice Duration to 00:00 for ${this.selectedReports.length} selected reports?`,
        reportIds: this.selectedReports
      }
    },
    cancelBatchUpdate () {
      this.confirmDialog = {
        show: false,
        title: '',
        message: '',
        reportIds: []
      }
    },
    addSelectedReport (e) {
      if (Array.isArray(e)) {
        for (let i = 0; i < e.length; i++) {
          this.addReportId(e[i])
        }
      } else {
        this.addReportId(e)
      }
    },
    removeSelectedReport (e) {
      if (Array.isArray(e)) {
        for (let i = 0; i < e.length; i++) {
          this.removeReportId(e[i])
        }
      } else {
        this.removeReportId(e)
      }
    },
    addReportId (repId) {
      const isInSelected = this.selectedReports.findIndex(r => r === repId)
      if (isInSelected === -1) {
        this.selectedReports.push(repId)
      }
      const emplIDs = [...new Set(this.reports.filter(r => this.selectedReports.indexOf(r.Reporting_ID) > -1).map(r => r.Employee_ID))]
      if (emplIDs && emplIDs.length === 1) {
        this.assignedTo = emplIDs[0]
      } else {
        this.assignedTo = -1
      }
    },
    removeReportId (repId) {
      const removeReportAt = this.selectedReports.findIndex(r => r === repId)
      if (removeReportAt > -1) {
        this.selectedReports.splice(removeReportAt, 1)
      }
      if (this.selectedReports.length === 0) {
        this.assignedTo = -1
      }
      const emplIDs = [...new Set(this.reports.filter(r => this.selectedReports.indexOf(r.Reporting_ID) > -1).map(r => r.Employee_ID))]
      if (emplIDs && emplIDs.length === 1) {
        this.assignedTo = emplIDs[0]
      } else {
        this.assignedTo = -1
      }
    },
    deleteReportsFromState (e) {
      this.reports = this.reports.filter(r => e.deletedReports.indexOf(r.Reporting_ID) === -1)
      const openGroups = this.groups.filter(gr => gr.open).map(gr => gr.key)
      this.setGroup(openGroups)
      this.setPagination()
      this.totalsForAllGroups()
      this.selectedReports = []
    },
    deleteReport (e) {
      this.deleteReportData.show = true
      if (Array.isArray(e)) {
        this.deleteReportData.reportID = e
      } else {
        this.deleteReportData.reportID = [e.Reporting_ID]
      }
    },
    scrollTop () {
      this.$refs.scrollAreaRef.scrollTo(0, 0)
    },
    setGroup (openGroups) {
      this.groups = []
      if (this.groupBy.length) {
        const groupTitles = [...new Set(this.filteredReports.map(r => {
          const title = []
          for (let i = 0; i < this.groupBy.length; i++) {
            title.push(r[this.groupBy[i]])
          }
          return title.join(', ')
        }))]
        groupTitles.forEach(gt => {
          this.groups.push({
            key: gt.split(', ').join('_'),
            title: gt,
            reports: [],
            reportsPerPage: this.reportsPerPage,
            open: openGroups && openGroups.length ? openGroups.indexOf(gt.split(', ').join('_')) > -1 : false
          })
          this.getReportsForGroup(this.groups[this.groups.length - 1])
        })
        this.groups.sort((a, b) => {
          if (a.title < b.title) return -1
          if (a.title > b.title) return 1
          return 0
        })
        if (this.groups.length > 100) {
          this.paginationForGroup.currentPage = 1
          this.paginationForGroup.totalPages = Math.ceil(this.groups.length / this.reportsPerPage)
        } else {
          this.paginationForGroup.currentPage = 1
          this.paginationForGroup.totalPages = null
        }
      } else {
        this.paginationForGroup.currentPage = 1
        this.paginationForGroup.totalPages = null
        this.groups = [{
          key: 'no-groups-key',
          title: null,
          reports: this.filteredReports,
          open: true,
          total: this.calculateTotals(this.filteredReports)
        }]
      }
    },
    changeCurrentPageForGroup (groupKey, page) {
      const currentPagination = { ...this.groupPagination(groupKey) }
      currentPagination.currentPage = page
      this.currentPage = page
      this.groupPagination = currentPagination
    },
    groupFieldChanged () {
      this.setGroup()
      this.setPagination()
      this.totalsForAllGroups()
    },
    setPagination () {
      this.pagination = []
      for (let i = 0; i < this.groups.length; i++) {
        if (this.groups[i].reports.length > this.reportsPerPage) {
          const indexOfPagination = this.pagination.findIndex(p => p.key === this.groups[i].key)
          if (indexOfPagination === -1) {
            this.pagination.push({
              key: this.groups[i].key,
              currentPage: Math.ceil(this.groups[i].reports.length / this.reportsPerPage) >= this.currentPage ? this.currentPage : 1,
              totalPages: Math.ceil(this.groups[i].reports.length / this.reportsPerPage),
              reportsPerPage: this.reportsPerPage
            })
          }
        }
      }
    },
    onCreateReport (e) {
      if (this.canReport(e.Project_Name)) {
        this.axios.post('/api/post-report-from-manager', e, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(res => {
          if (res.data[0] && this.canShowReports) {
            const createdReportStartDate = +new Date(res.data[0].Start_Date)
            const from = +new Date(this.dateRange[0])
            const to = +new Date(this.dateRange[1])
            if ((from <= createdReportStartDate && to >= createdReportStartDate) || (!from && !to)) {
              this.reports.push(res.data[0])
              const openGroups = this.groups.filter(gr => gr.open).map(gr => gr.key)
              this.setGroup(openGroups)
              this.setPagination()
              this.totalsForAllGroups()
            } else {
              this.alertDialog = true
              this.alertDialogMsg = 'A report was created but not displayed because it does not match the filter criteria'
            }
          }
          if (this.reportingFor) {
            this.reportingFor = null
          }
          this.$store.dispatch('setSuccess', { successMsg: 'Report succesfully updated' })
        }).catch(error => {
          this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
        })
      }
    },
    onUpdateReport (e) {
      if (this.canReport(e.Project_Name)) {
        this.axios.put(`/api/update-report-from-manager/${e.Reporting_ID}`, {
          Project_ID: e.Project_ID,
          Task_ID: e.Task_ID,
          Employee_ID: e.Employee_ID,
          Start_Date: e.Start_Date,
          Start_Time: '00:00',
          Real_Dur: e.Real_Dur,
          Report_Dur: e.Report_Dur,
          Story_Points: e.Story_Points,
          Timespent: e.Timespent,
          Status_Name: e.Status_Name,
          Invoice_Dur: e.Invoice_Dur,
          Description: e.Description,
          Activity_Type: e.Activity_Type,
          Locked: e.Locked ?? false
        }, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(res => {
          const repToUpdate = this.reports.findIndex(r => r.Reporting_ID === e.Reporting_ID)
          this.$set(this.reports, repToUpdate, res.data[0])
          const openGroups = this.groups.filter(gr => gr.open).map(gr => gr.key)
          this.setGroup(openGroups)
          this.setPagination()
          this.totalsForAllGroups()
          this.$store.dispatch('setSuccess', { successMsg: 'Report succesfully updated' })
          this.$emit('reportsUpdated', e.Project_ID)
        }).catch(error => {
          this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
        })
      }
    },
    onRowClick (e) {
      this.editReport = {
        ...e,
        show: true
      }
    },
    onContextMenu (e) {
      this.contextMenu.show = false
      this.contextMenu.x = e.x
      this.contextMenu.y = e.y
      this.contextMenu.report = e.report
      this.$nextTick(() => {
        this.contextMenu.show = true
      })
    },
    toggleGroup (group) {
      group.open = !group.open
    },
    getReportsForGroup (group) {
      for (let i = 0; i < this.filteredReports.length; i++) {
        const title = []
        const r = this.filteredReports[i]
        for (let j = 0; j < this.groupBy.length; j++) {
          title.push(r[this.groupBy[j]])
        }
        if (title.join(', ') === group.title) {
          group.reports.push(r)
        }
      }
      const totals = {
        totalReported: 0,
        totalPaid: 0,
        totalInvoice: 0,
        vacationLeft: 0,
        sickLeaveLeft: 0
      }
      group.reports.forEach(r => {
        const reportDur = r.Real_Dur.split(':')
        const paidDur = r.Report_Dur.split(':')
        const invoiceDur = r.Invoice_Dur.split(':')
        const reportH = +reportDur[0]
        const reportM = +reportDur[1] * 100 / 60 / 100
        const paidH = +paidDur[0]
        const paidM = +paidDur[1] * 100 / 60 / 100
        const invoiceH = +invoiceDur[0]
        const invoiceM = +invoiceDur[1] * 100 / 60 / 100
        const reportDurFloat = reportH + reportM
        const paidDurFloat = paidH + paidM
        const invoiceDurFloat = invoiceH + invoiceM
        totals.totalReported += reportDurFloat
        totals.vacationLeft += +r.Vacation_Left
        totals.sickLeaveLeft += +r.Sick_Leave_Left
        totals.totalPaid += paidDurFloat
        totals.totalInvoice += invoiceDurFloat
      })
      group.total = this.calculateTotals(group.reports)
    },
    calculateTotals (reports) {
      const totals = {
        totalReported: 0,
        totalPaid: 0,
        totalInvoice: 0,
        vacationLeft: 0,
        sickLeaveLeft: 0
      }
      reports.forEach(r => {
        const reportDur = r.Real_Dur.split(':')
        const paidDur = r.Report_Dur.split(':')
        const invoiceDur = r.Invoice_Dur.split(':')
        const reportH = +reportDur[0]
        const reportM = +reportDur[1] * 100 / 60 / 100
        const paidH = +paidDur[0]
        const paidM = +paidDur[1] * 100 / 60 / 100
        const invoiceH = +invoiceDur[0]
        const invoiceM = +invoiceDur[1] * 100 / 60 / 100
        const reportDurFloat = reportH + reportM
        const paidDurFloat = paidH + paidM
        const invoiceDurFloat = invoiceH + invoiceM
        totals.totalReported += reportDurFloat
        totals.totalPaid += paidDurFloat
        totals.totalInvoice += invoiceDurFloat
        totals.vacationLeft = +r.Vacation_Left
        totals.sickLeaveLeft = +r.Sick_Leave_Left
      })
      return totals
    },
    isGroupOpen (key) {
      // eslint-disable-next-line no-prototype-builtins
      if (!this.groupToggle.hasOwnProperty(key)) {
        this.$set(this.groupToggle, key, false)
      }
      return this.groupToggle[key]
    },
    onChangeSearchVal () {
      const openGroups = this.groups.filter(gr => gr.open).map(gr => gr.key)
      this.setGroup(openGroups)
      this.setPagination()
      this.totalsForAllGroups()
    },
    async getReports (fromStore) {
      this.loading = true
      await this.axios.post('/api/get-manager-reports', {
        ...this.value,
        dateFrom: this.dateRange[0] ? this.dateRange[0] : null,
        dateTo: this.dateRange[1] ? this.dateRange[1] : null,
        filterCompleted: this.filterClosedFoldersTasks
      }, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.reportsItemKey = Date.now()
        this.reports.splice()
        this.reports = [...res.data]
        let openGroups = []
        if (fromStore) {
          this.updateReportsList = false
          openGroups = this.groups.filter(gr => gr.open).map(gr => gr.key)
        }
        this.setGroup(openGroups)
        this.setPagination()
        this.totalsForAllGroups()
      }).catch(error => {
        this.$store.dispatch('setError', { errorMsg: error.message + ' - ' + error.response.data })
      }).finally(() => {
        this.loading = false
      })
    },
    async onColumnConfigSave (payload) {
      this.columnConfig = payload
      await this.saveTableColumnConfig(this.tableColumnConfigKey, payload)
    },
    exportToXLSX (group) {
      const data = []
      const columns = this.resolvedColumns.filter(c => c.exportable).map(h => {
        return {
          label: h.title,
          value: h.key
        }
      })
      const allTabData = []
      group = group.length ? group : [group]
      let itemsToExport = []
      for (let i = 0; i < group.length; i++) {
        for (let j = 0; j < group[i].reports.length; j++) {
          itemsToExport.push(group[i].reports[j])
        }
        // Total row
        const totalRow = {}
        const notExportableKeys = this.resolvedColumns.filter(c => !c.exportable).map(c => c.key)
        for (const key in group[i].reports[0]) {
          if (notExportableKeys.indexOf(key) > -1) {
            continue
          }
          if (key === 'Report_Dur') {
            totalRow[key] = group[i].total.totalPaid
            continue
          }
          if (key === 'Real_Dur') {
            totalRow[key] = group[i].total.totalReported
            continue
          }
          if (key === 'Invoice_Dur') {
            totalRow[key] = group[i].total.totalInvoice
            continue
          }
          if (key === 'Vacation_Left') {
            totalRow[key] = group[i].total.vacationLeft
            continue
          }
          if (key === 'Sick_Leave_Left') {
            totalRow[key] = group[i].total.sickLeaveLeft
            continue
          }
          totalRow[key] = ''
        }
        itemsToExport.push(totalRow)
        allTabData.push(...itemsToExport)
        const indexToString = '' + (i + 1)
        let title = (indexToString + '.Reports ' + (group[i].title || 'All')).trim()
        if (title.length > 31) {
          title = title.substring(0, 28) + '...'
        }
        title = title
          .replace(/[\\/]/g, '|') // Replace / and \ with |
          .replace(/[?*]/g, '') // Remove ? and *
          .replace(/:/g, '-') // Replace : with -
          .replace(/\[/g, '(') // Replace [ with (
          .replace(/\]/g, ')') // Replace ] with )
        data.push({
          sheet: title,
          columns: columns,
          content: itemsToExport
        })
        itemsToExport = []
      }
      if (group && group.length > 1) {
        data.unshift({
          sheet: 'All',
          columns,
          content: [...allTabData]
        })
      }
      xlsx(data, { fileName: 'Reports' })
    },
    getRedirectedReports () {
      if (this.isRedirected) {
        this.dateRange = [moment().startOf('month').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')]
        this.getReports(false)
      }
    }
  },
  watch: {
    refreshResultsKey (newVal) {
      setTimeout(() => {
        if (
          this.value &&
          (
            (this.value.selectedEmployee && this.value.selectedEmployee.length) ||
            (this.value.selectedFolders && this.value.selectedFolders.length) ||
            (this.value.selectedProjects && this.value.selectedProjects.length) ||
            (this.value.selectedTask && this.value.selectedTask.length)
          ) &&
          this.reports.length
        ) {
          this.getReports(true)
        }
      })
    },
    reportToUpdate (newVal) {
      if (newVal !== null) {
        this.onUpdateReport(newVal)
        this.updateReportsList = null
      }
    }
  }
}
</script>
<style lang="scss" scoped>
  .pagination-container {
    position: sticky;
    bottom: 0;
    z-index: 2;
    background-color: #fff;
    border-top: 1px solid #d7d7d7;
  }
  .group-title {
    position: sticky;
    top: 0;
    left: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    padding: 0 5px;
    border-bottom: 1px solid #ffffff!important;
    cursor: pointer;
    user-select: none;
    width: 100%;
  }
    .group-container {
      min-height: 100%;
      &--auto {
        min-height: unset;
      }
    }
    .export-to-xlsx-btn {
      width: 135px;
      flex-shrink: 0;
    }
    .grand-total {
      padding: 0 5px;
      margin-bottom: 2px;
      display: flex;
      align-items: center;
    }

  .total-flex-container {
    display: flex;
    align-items: center;
    text-align: right;
    flex-shrink: 1;
  }

  .total-flex-col {
    display: flex;
    flex-direction: column;
    padding: 0 8px;
    white-space: nowrap;
    flex-shrink: 0;
    box-sizing: border-box;
  }

  .col-reported, .col-paid, .col-invoice {
    width: 90px;
    min-width: 90px;
  }
  .col-vacation, .col-sick {
    width: 110px;
    min-width: 110px;
  }

  .total-flex-header {
    font-size: 0.7rem;
    color: #616161;
    font-weight: normal;
    line-height: 1.2;
  }

  .total-flex-value {
    font-size: 0.8rem;
    font-weight: 800;
    line-height: 1.2;
  }

  .total-flex-col--group {
    justify-content: center;
    min-height: 30px;
    padding: 0 8px;
  }

  .theme--light .pagination-container {
    background-color: #fff;
    border-top: 1px solid #d7d7d7;
  }

  .theme--dark .pagination-container {
    background-color: #1e1e1e;
    border-top: 1px solid #333;
  }
</style>
